import { useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";
import { api, errMsg, imgSrc } from "../../lib/api";
import { LISTING_TYPES, CITIES, STATUSES, STRATEGIES, DISTRESS_TYPES, TIMELINES, METRIC_LABELS } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Chips, Field } from "../Common";

const NUMS = ["asking_price", "est_acquisition_price", "sqft", "units", "occupancy", "noi", "cap_rate", "lat", "lng"];
const LABELED = ["asking_price", "est_acquisition_price", "noi", "cap_rate", "occupancy", "sqft"];
const BLANK = { title: "", property_type: "Office", city: "Portland", county: "", address: "", category: "standard", status: "New", summary: "", lot_size: "", rent_info: "", operating_expenses: "", value_add: "", investment_thesis: "", key_considerations: [], due_diligence_status: "", strategies: [], distress_type: "", closing_timeline: "", featured: false, images: [], video_url: "", confidential_notes: "", metric_labels: {} };

const toForm = (p) => {
  const f = { ...BLANK, ...p };
  NUMS.forEach((k) => (f[k] = p?.[k] ?? ""));
  f.kc = (p?.key_considerations || []).join("\n");
  f.imgs = (p?.images || []).join("\n");
  return f;
};

function Uploads({ p, onChange }) {
  const [busy, setBusy] = useState(false);
  const up = (kind) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/properties/${p.id}/${kind}`, fd);
      toast.success("Uploaded");
      onChange();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const delDoc = async (id) => { await api.delete(`/admin/properties/${p.id}/documents/${id}`); onChange(); };
  return (
    <div className="space-y-4 border-t border-navy/10 pt-6" data-testid="property-uploads">
      <p className="eyebrow">Photos & Documents {busy && "· uploading…"}</p>
      <div className="flex flex-wrap gap-2">{p.images.map((s) => <img key={s} src={imgSrc(s)} alt="" className="h-16 w-20 object-cover" />)}</div>
      <div className="flex flex-wrap gap-3">
        <label className="btn-ghost text-navy cursor-pointer !py-2.5"><Upload className="h-4 w-4" /> Upload photo<input type="file" accept="image/*" className="hidden" onChange={up("images")} data-testid="admin-upload-image" /></label>
        <label className="btn-ghost text-navy cursor-pointer !py-2.5"><Upload className="h-4 w-4" /> Upload document<input type="file" className="hidden" onChange={up("documents")} data-testid="admin-upload-doc" /></label>
      </div>
      <ul className="text-sm space-y-1">
        {(p.documents || []).filter((d) => !d.is_deleted).map((d) => (
          <li key={d.id} className="flex justify-between bg-paper px-3 py-2"><span>{d.name}</span><button type="button" onClick={() => delDoc(d.id)} aria-label="Remove document"><Trash2 className="h-4 w-4 text-mist" /></button></li>
        ))}
      </ul>
      <p className="text-xs text-mist">Documents are only visible to admins and investors with approved access requests.</p>
    </div>
  );
}

export default function PropertyEditor({ open, onClose, property, onSaved }) {
  const [f, setF] = useState(toForm(property));
  const [current, setCurrent] = useState(property);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const I = (k, props = {}) => <input className="field !py-2.5 text-sm" value={f[k] ?? ""} onChange={set(k)} data-testid={`pe-${k.replace(/_/g, "-")}`} {...props} />;
  const S = (k, opts, blank) => <select className="field !py-2.5 text-sm" value={f[k] ?? ""} onChange={set(k)} data-testid={`pe-${k.replace(/_/g, "-")}`}>{blank && <option value="">—</option>}{opts.map((o) => <option key={o}>{o}</option>)}</select>;
  const T = (k, props = {}) => <textarea className="field !py-2.5 text-sm min-h-[80px]" value={f[k] ?? ""} onChange={set(k)} data-testid={`pe-${k.replace(/_/g, "-")}`} {...props} />;

  const refresh = async () => {
    const { data } = await api.get("/admin/properties");
    const p = data.find((x) => x.id === current.id);
    setCurrent(p);
    setF((s) => ({ ...s, images: p.images, imgs: p.images.join("\n") }));
    onSaved();
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, key_considerations: f.kc.split("\n").map((x) => x.trim()).filter(Boolean), images: f.imgs.split("\n").map((x) => x.trim()).filter(Boolean) };
    NUMS.forEach((k) => (body[k] = body[k] === "" || body[k] == null ? null : Number(body[k])));
    try {
      const { data } = current ? await api.put(`/admin/properties/${current.id}`, body) : await api.post("/admin/properties", body);
      toast.success(current ? "Property updated" : "Property created — you can now upload photos & documents");
      setCurrent(data);
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none" data-testid="property-editor">
        <DialogHeader><DialogTitle className="font-serif text-3xl text-navy">{current ? "Edit Property" : "Add Property"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Title *" className="sm:col-span-2">{I("title", { required: true })}</Field>
            <Field label="Property type">{S("property_type", LISTING_TYPES)}</Field>
            <Field label="Status">{S("status", STATUSES)}</Field>
            <Field label="City">{S("city", CITIES)}</Field>
            <Field label="County">{I("county")}</Field>
            <Field label="Category">{S("category", ["standard", "distressed"])}</Field>
            <Field label="Confidential address (gated)">{I("address")}</Field>
            {f.category === "distressed" && <Field label="Distress type">{S("distress_type", DISTRESS_TYPES, true)}</Field>}
            {f.category === "distressed" && <Field label="Closing timeline">{S("closing_timeline", TIMELINES, true)}</Field>}
          </div>
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-4">
            {[["asking_price", "Asking $"], ["est_acquisition_price", "Est. acquisition $"], ["sqft", "SF"], ["units", "Units"], ["occupancy", "Occupancy %"], ["noi", "NOI $"], ["cap_rate", "Cap rate %"]].map(([k, l]) => <Field key={k} label={l}>{I(k, { inputMode: "decimal" })}</Field>)}
            <Field label="Lot size">{I("lot_size")}</Field>
          </div>
          <div>
            <span className="label">Metric labels</span>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
              {LABELED.map((k) => (
                <select key={k} className="field !py-2 text-xs" value={f.metric_labels?.[k] || ""} onChange={(e) => setF({ ...f, metric_labels: { ...f.metric_labels, [k]: e.target.value } })} data-testid={`pe-label-${k}`}>
                  <option value="">{k.replace(/_/g, " ")}: no label</option>{METRIC_LABELS.map((o) => <option key={o} value={o}>{k.replace(/_/g, " ")}: {o}</option>)}
                </select>
              ))}
            </div>
          </div>
          <div><span className="label">Strategies</span><Chips options={STRATEGIES} value={f.strategies} onChange={(v) => setF({ ...f, strategies: v })} testid="pe-strategy" /></div>
          <Field label="Overview">{T("summary")}</Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Rent information">{T("rent_info")}</Field>
            <Field label="Operating expenses">{T("operating_expenses")}</Field>
            <Field label="Renovation / value-add">{T("value_add")}</Field>
            <Field label="Investment thesis">{T("investment_thesis")}</Field>
            <Field label="Key considerations (one per line)">{T("kc")}</Field>
            <Field label="Confidential notes (gated)">{T("confidential_notes")}</Field>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Due diligence status">{I("due_diligence_status")}</Field>
            <Field label="Video URL">{I("video_url")}</Field>
            <Field label="Map latitude (optional)" hint="Leave blank to use an approximate city-level pin.">{I("lat", { inputMode: "decimal" })}</Field>
            <Field label="Map longitude (optional)">{I("lng", { inputMode: "decimal" })}</Field>
          </div>
          <Field label="Image URLs (one per line)">{T("imgs")}</Field>
          <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={!!f.featured} onChange={set("featured")} data-testid="pe-featured" /> Featured on homepage</label>
          <button disabled={busy} className="btn-primary w-full" data-testid="pe-save">{busy ? "Saving…" : "Save Property"}</button>
        </form>
        {current && <Uploads p={current} onChange={refresh} />}
      </DialogContent>
    </Dialog>
  );
}
